import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { ShareholderActivationService } from '../../core/services/api/shareholder-activation.service';

@Component({
  selector: 'app-shareholder-activate',
  templateUrl: './shareholder-activate.component.html',
  styleUrls: ['./shareholder-activate.component.scss'],
  standalone: false,
})
export class ShareholderActivateComponent implements OnInit {
  isLoading = true;
  errorMessage: string;
  // True once the backend confirms the token and the redirect target is /account/setup,
  // which doesn't exist in this app yet (SEC-285) — shown as a stopgap instead of a 404.
  accountSetupPending = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private shareholderActivationService: ShareholderActivationService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit() {
    const token = this.activatedRoute.snapshot.queryParams.token;

    if (!token) {
      this.isLoading = false;
      this.errorMessage = 'Activation token is required.';
      return;
    }

    if (isPlatformBrowser(this.platformId)) this.activate(token);
  }

  private activate(token: string) {
    this.shareholderActivationService
      .activate(token)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (data) => {
          const redirectUrl = data.redirectUrl;

          if (!redirectUrl) {
            this.errorMessage = 'This activation link is invalid.';
            return;
          }

          if (redirectUrl.includes('/account/setup')) {
            this.accountSetupPending = true;
            return;
          }

          // Only allow in-app redirects to avoid open-redirects.
          const target = this.toSameSiteUrl(redirectUrl);
          if (!target) {
            this.errorMessage = 'Invalid activation redirect URL.';
            return;
          }

          this.redirectTo(target.href);
        },
        (err) => {
          this.errorMessage = err?.message || 'This activation link is invalid.';
        },
      );
  }

  // Thin wrapper so the spec can stub navigation: location.replace is non-configurable in modern
  // browsers, so spyOn(window.location, 'replace') throws and no test in this file could run.
  redirectTo(url: string): void {
    window.location.replace(url);
  }

  // The API builds redirectUrl as an absolute URL from AppSettings:WebClientBaseUrl (e.g.
  // https://staging.pitchin.dev/reset-password/...), so a bare startsWith('/') check rejected
  // every legitimate redirect. Accept relative paths and absolute URLs on this page's own origin -
  // which is always the API's WebClientBaseUrl, because the emailed link is built from that same value.
  // Anything else (other hosts, protocol-relative "//host", "/\host", javascript: etc.) stays blocked.
  // Returns the parsed URL (not just a boolean) so we navigate to exactly what was validated: a bare
  // relative value like "reset-password/x" would otherwise be checked against the origin root but
  // resolved by location.replace against the current page (/shareholder/activate).
  private toSameSiteUrl(url: string): URL | null {
    try {
      const target = new URL(url, window.location.origin);
      return target.origin === window.location.origin ? target : null;
    } catch {
      return null;
    }
  }
}
